import React, { useState, useEffect, useRef } from 'react';
import {
  Container,
  Card,
  Typography,
  Box,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Stack,
  Select,
  MenuItem,
  FormControl,
} from '@mui/material';
import { CloudUpload, Description, Search, Refresh } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import moment from 'moment';
import api from '../../../services/api';
import { sendDataWithPost } from '../../../services/services';
import { showErrorMsg, showSuccessMsg } from '../../../utils/toast';
import HeaderHelpPage from '../../../components/HeaderHelpPage';
import CustomDatePicker from '../../../components/form/CustomDatePicker';
import CircularLoader from '../../../components/circularLoader';
import ButtonCustom from '../../../components/ButtonCustom';
import { COLORS } from '../../../utils/colors';
import { styles } from '../../../utils/styles';
import { StorageUtils } from '../../../utils/index';
import ShowDetail from '../../../components/ShowDetail';
import { APP_USER_SESSION } from '../../../constant/AppConstants';

const bankStatementAcquirers = [
  { id: 'alipay', name: 'Alipay' },
  { id: 'atome', name: 'Atome' },
  { id: 'dash', name: 'Dash' },
  { id: 'fiuu', name: 'Fiuu' },
  { id: 'fomopay', name: 'FOMOPay' },
  { id: 'grabpay', name: 'GrabPay' },
  { id: 'paynow', name: 'PayNow' },
  { id: 'shopee', name: 'ShopeePay' },
  { id: 'wechat', name: 'WeChat' }
];


const UploadDialog = ({ open, onClose, onFileUploaded }) => {
  const fileInputRef = useRef(null);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    if (file) {
      const ext = (file.name || '').toLowerCase().split('.').pop();
      if (['xlsx', 'xls', 'csv'].includes(ext)) {
        setSelectedFile(file);
      } else {
        showErrorMsg('Only .xlsx, .xls, and .csv are allowed.');
        setSelectedFile(null);
      }
    }
  };

  const handleUploadFile = () => {
    if (!selectedFile) {
      showErrorMsg('Please select a file.');
      return;
    }
    const formData = new FormData();
    formData.append('file', selectedFile);
    setUploadLoading(true);

    const handleSuccess = (response) => {
      showSuccessMsg(response?.data?.detail || 'Bank statement uploaded successfully.');
      setSelectedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      onFileUploaded();
      setUploadLoading(false);
      onClose();
    };

    api
      .post('/payout/ingestor/bank_statement_upload/', formData, {
        transformRequest: [(data, headers) => {
          delete headers['Content-Type'];
          return data;
        }],
      })
      .then((response) => {
        if (response?.status === 200 || response?.status === 201) {
          handleSuccess(response);
        } else {
          showErrorMsg(response?.data?.detail?.toString() || response?.data?.toString());
          setUploadLoading(false);
        }
      })
      .catch((error) => {
        setUploadLoading(false);
        showErrorMsg(error.response?.data?.detail ?? error.message ?? 'Upload failed');
      });
  };

  const handleClose = () => {
    if (!uploadLoading) {
      setSelectedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
      <DialogTitle sx={{ textAlign: 'center', pb: 1 }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Upload Bank Statement
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ px: 3, py: 2 }}>
        <Box
          sx={{
            border: '2px dashed',
            borderColor: selectedFile ? 'success.main' : 'grey.300',
            borderRadius: 2,
            p: 2,
            textAlign: 'center',
            backgroundColor: selectedFile ? 'success.light' : 'grey.50',
            cursor: 'pointer',
            '&:hover': {
              borderColor: selectedFile ? 'success.dark' : 'primary.main',
              backgroundColor: selectedFile ? 'success.light' : 'primary.50',
            },
          }}
          onClick={() => fileInputRef.current?.click()}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,.xlsx,.xls"
            onChange={handleFileChange}
            style={{ display: 'none' }}
          />
          {selectedFile ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 50, height: 50, borderRadius: '50%', backgroundColor: 'success.main', mb: 1 }}>
                <Description sx={{ color: 'white', fontSize: 24 }} />
              </Box>
              <Typography variant="body1" color="success.main" sx={{ fontWeight: 600, mb: 0.5 }}>
                {selectedFile.name}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 50, height: 50, borderRadius: '50%', backgroundColor: 'primary.main', mb: 1 }}>
                <CloudUpload sx={{ color: 'white', fontSize: 24 }} />
              </Box>
              <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                Choose a file
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Supported formats: XLSX, XLS, CSV
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={handleClose} disabled={uploadLoading} variant="outlined" sx={{ mr: 1 }}>
          Cancel
        </Button>
        <LoadingButton
          variant="contained"
          loading={uploadLoading}
          disabled={!selectedFile}
          onClick={handleUploadFile}
          sx={{ backgroundColor: COLORS.OXYGEN7_PURPLE, '&:hover': { backgroundColor: 'rgba(91, 45, 130, 0.8)' } }}
          startIcon={<CloudUpload />}
        >
          Upload Bank Statement
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

const BankStatementsTable = ({ statements, loading, page, rowsPerPage }) => {
  const formatAmount = (amount) => {
    return Number(amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const paginatedStatements = statements.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <TableContainer sx={{ overflowX: 'auto', display: 'block' }}>
      <Table sx={{ width: '100%' }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ whiteSpace: 'nowrap' }}><Typography variant="subtitle2" fontWeight={600}>Payout Date</Typography></TableCell>
            <TableCell sx={{ whiteSpace: 'nowrap' }}><Typography variant="subtitle2" fontWeight={600}>Acquirer</Typography></TableCell>
            <TableCell sx={{ whiteSpace: 'nowrap' }}><Typography variant="subtitle2" fontWeight={600}>Account Number</Typography></TableCell>
            <TableCell sx={{ whiteSpace: 'nowrap' }}><Typography variant="subtitle2" fontWeight={600}>Account Name</Typography></TableCell>
            <TableCell sx={{ whiteSpace: 'nowrap' }}><Typography variant="subtitle2" fontWeight={600}>Currency</Typography></TableCell>
            <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}><Typography variant="subtitle2" fontWeight={600}>Withdrawal</Typography></TableCell>
            <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}><Typography variant="subtitle2" fontWeight={600}>Deposit</Typography></TableCell>
            <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}><Typography variant="subtitle2" fontWeight={600}>Balance</Typography></TableCell>
            <TableCell><Typography variant="subtitle2" fontWeight={600}>Description</Typography></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {loading ? (
            <TableRow>
              <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                <CircularProgress />
              </TableCell>
            </TableRow>
          ) : paginatedStatements.length === 0 ? (
            <TableRow>
              <TableCell colSpan={9} align="center" sx={{ py: 3 }}>
                <Typography gutterBottom align="center" variant="subtitle1">
                  Not found
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            paginatedStatements.map((statement, index) => (
              <TableRow key={index} hover>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  <Typography variant="body2" noWrap>{statement.payout_date}</Typography>
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  <Typography variant="body2" noWrap>{statement.acquirer}</Typography>
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  <Typography variant="body2" noWrap>{statement.account_number}</Typography>
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  <Typography variant="body2" noWrap>{statement.account_name}</Typography>
                </TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                  <Typography variant="body2" noWrap>{statement.currency}</Typography>
                </TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                  <Typography variant="body2" align="right" noWrap>{formatAmount(statement.withdrawal_amount)}</Typography>
                </TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                  <Typography variant="body2" align="right" noWrap>{formatAmount(statement.deposit_amount)}</Typography>
                </TableCell>
                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                  <Typography variant="body2" align="right" noWrap>{formatAmount(statement.balance_amount)}</Typography>
                </TableCell>
                <TableCell>
                  <ShowDetail text={statement.description} limit={50} />
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const BankStatements = () => {
  const [payoutDate, setPayoutDate] = useState(new Date());
  const [acquirer, setAcquirer] = useState('');
  const [statements, setStatements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [openUploadDialog, setOpenUploadDialog] = useState(false);

  const userRole = StorageUtils.getParsedValue(APP_USER_SESSION)?.role ?? {};
  const bankStatementRole = userRole.bank_statement || [];
  const canUpload = bankStatementRole.includes('Upload file');

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getFilterData = () => ({
    payout_date: moment(payoutDate),
    acquirer,
  });

  const handleSearch = () => {
    setPage(0);
    loadStatements(getFilterData());
  };

  const handleReset = () => {
    setPayoutDate(new Date());
    setAcquirer(null);
    setPage(0);
  };

  const handleFileUploaded = () => {
    loadStatements(getFilterData());
  };

  const loadStatements = async (filterData) => {
    setLoading(true);
    const requestData = {};
    if (filterData.payout_date && moment(filterData.payout_date).isValid()) {
      requestData.payout_date = moment(filterData.payout_date).format('YYYY-MM-DD');
    }
    if (filterData.acquirer) {
      requestData.acquirer = filterData.acquirer;
    }
    const handleSuccess = (response) => {
      let allStatements = [];
      if (response.data?.result) {
        allStatements = response.data.result;
      }
      setStatements(allStatements);
      setLoading(false);
    };
    sendDataWithPost('/payout/ingestor/files_uploaded_bank_statement/', requestData, null, () => setLoading(false), handleSuccess, () => { showErrorMsg('Failed to load bank statements'); setLoading(false); });
  };

  useEffect(() => {
    loadStatements(getFilterData());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Container maxWidth="xl" sx={{ mt: 2, mb: 4 }}>
      <HeaderHelpPage title="Bank Statements" isShowLink={false} />
      <Card sx={{ mb: 2, px: 2, py: 2, borderRadius: 2, border: '1px solid #f0f0f0' }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={3}>
            <Box>
              <Typography sx={{ mb: 0.5, pb: 0.5, fontSize: 15, fontWeight: 500 }} variant="subtitle1">
                Payout Date
              </Typography>
              <CustomDatePicker
                value={payoutDate}
                onChange={(newValue) => setPayoutDate(newValue)}
                fullWidth
                size="small"
                views={["year", "month", "day"]}
                openTo="day"
                sx={{ '& .MuiOutlinedInput-root': { height: '30px', fontSize: '14px' } }}
              />
            </Box>
          </Grid>
          <Grid item xs={12} sm={3}>
            <Box>
              <Typography sx={{ mb: 0.5, pb: 0.5, fontSize: 15, fontWeight: 500 }} variant="subtitle1">
                Acquirer
              </Typography>
              <FormControl fullWidth>
                <Select
                  value={acquirer}
                  onChange={(e) => setAcquirer(e.target.value)}
                  size="small"
                  displayEmpty
                  sx={{
                    height: '30px',
                    '& .MuiSelect-select': { fontSize: '14px' },
                  }}
                  renderValue={(selected) => {
                    if (!selected) return 'All Acquirers';
                    const opt = bankStatementAcquirers.find((o) => o.id === selected);
                    return opt ? opt.name : selected;
                  }}
                >
                  <MenuItem value="">All Acquirers</MenuItem>
                  {bankStatementAcquirers.map((item) => (
                    <MenuItem key={item.id} value={item.id}>
                      {item.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Grid>
          {canUpload && (
            <Grid item xs={12} sm={3}>
              <Box sx={{ display: 'flex', alignItems: 'flex-end', height: '100%' }}>
                <LoadingButton
                  variant="contained"
                  size="small"
                  sx={{
                    backgroundColor: COLORS.OXYGEN7_PURPLE,
                    '&:hover': { backgroundColor: 'rgba(91, 45, 130, 0.8)' },
                  }}
                  onClick={() => setOpenUploadDialog(true)}
                >
                  Upload
                </LoadingButton>
              </Box>
            </Grid>
          )}
        </Grid>
        <Stack direction="row" justifyContent="flex-end">
          <ButtonCustom
            sx={{ ml: 1, mt: 2, ...styles.customButton }}
            icon={<Search />}
            variant="contained"
            title="Search"
            onClick={handleSearch}
          />
          <ButtonCustom
            sx={{ ml: 1, mt: 2 }}
            icon={<Refresh />}
            title="Reset"
            onClick={handleReset}
          />
        </Stack>
      </Card>
      {loading ? (
        <CircularLoader />
      ) : (
        <Card sx={{ px: 2, py: 2, borderRadius: 2, border: '1px solid #f0f0f0', position: 'relative' }}>
          <BankStatementsTable
            statements={statements}
            loading={false}
            page={page}
            rowsPerPage={rowsPerPage}
          />
          <TablePagination
            rowsPerPageOptions={[25, 50, 100]}
            component="div"
            count={statements.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Card>
      )}
      {canUpload && (
        <UploadDialog
          open={openUploadDialog}
          onClose={() => setOpenUploadDialog(false)}
          onFileUploaded={handleFileUploaded}
        />
      )}
    </Container>
  );
};

export default BankStatements;
